import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = '/api';

const DAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const DAY_LABELS = {
  lundi: 'Lundi',
  mardi: 'Mardi',
  mercredi: 'Mercredi',
  jeudi: 'Jeudi',
  vendredi: 'Vendredi',
  samedi: 'Samedi',
};

const TIME_SLOTS = [
  { start: '08:00', end: '10:00' },
  { start: '10:00', end: '12:00' },
  { start: '12:00', end: '14:00' },
  { start: '14:00', end: '16:00' },
  { start: '16:00', end: '18:00' },
];

// ========== COMPOSANT NOTIFICATION ==========
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === 'success'
      ? 'bg-green-500'
      : type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';

  const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️';

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        animation: 'slideInRight 0.3s ease-out',
      }}
      className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px] max-w-[500px]`}
    >
      <span className="text-xl">{icon}</span>
      <span className="flex-1 font-medium text-sm">{message}</span>
      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 text-2xl font-bold leading-none ml-2"
        title="Fermer"
      >
        ×
      </button>
    </div>
  );
};

export default function AdminSchedule() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedClass, setSelectedClass] = useState('');
  const [academicYear, setAcademicYear] = useState('2026-2027');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    class_id: '',
    subject_id: '',
    teacher_id: '',
    day_of_week: 'lundi',
    start_time: '08:00',
    end_time: '10:00',
    room: '',
    academic_year: '2026-2027',
  });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    loadClasses();
    loadSubjects();
    loadTeachers();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadSchedules();
    } else {
      setSchedules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, academicYear]);

  const loadClasses = async () => {
    try {
      const res = await axios.get(`${API_URL}/classes`, axiosConfig);
      setClasses(res.data);
    } catch (e) {
      console.error('Erreur classes:', e);
    }
  };

  const loadSubjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/subjects`, axiosConfig);
      setSubjects(res.data);
    } catch (e) {
      console.error('Erreur matières:', e);
    }
  };

  const loadTeachers = async () => {
    try {
      const res = await axios.get(`${API_URL}/teachers`, axiosConfig);
      setTeachers(res.data);
    } catch (e) {
      console.error('Erreur profs:', e);
    }
  };

  const loadSchedules = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/admin/schedules?class_id=${selectedClass}&academic_year=${academicYear}`,
        axiosConfig
      );
      setSchedules(res.data);
    } catch (e) {
      console.error('Erreur schedules:', e);
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = (day, slot) => {
    console.log('➕ Création pour:', day, slot);
    setEditingId(null);
    setForm({
      class_id: selectedClass,
      subject_id: '',
      teacher_id: '',
      day_of_week: day,
      start_time: slot.start,
      end_time: slot.end,
      room: '',
      academic_year: academicYear,
    });
    setShowModal(true);
  };

  const openEditModal = (schedule) => {
    console.log('✏️ Modification du cours ID:', schedule.id);
    setEditingId(schedule.id);
    setForm({
      class_id: schedule.class_id,
      subject_id: schedule.subject_id,
      teacher_id: schedule.teacher_id || '',
      day_of_week: schedule.day_of_week,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      room: schedule.room || '',
      academic_year: schedule.academic_year,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('💾 Submit - editingId:', editingId, 'form:', form);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/admin/schedules/${editingId}`, form, axiosConfig);
        showToast('Cours modifié avec succès !', 'success');
      } else {
        await axios.post(`${API_URL}/admin/schedules`, form, axiosConfig);
        showToast('Cours ajouté avec succès !', 'success');
      }
      setShowModal(false);
      loadSchedules();
    } catch (e) {
      console.error('Erreur submit:', e);
      const msg = e.response?.data?.error || "Erreur lors de l'enregistrement";
      showToast(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    console.log('🗑️ Suppression du cours ID:', id);

    if (!window.confirm('Voulez-vous vraiment supprimer ce cours ?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/schedules/${id}`, axiosConfig);
      showToast('Cours supprimé avec succès !', 'success');
      loadSchedules();
    } catch (e) {
      console.error('Erreur suppression:', e);
      const msg = e.response?.data?.error || 'Erreur lors de la suppression';
      showToast(msg, 'error');
    }
  };

  const getScheduleForSlot = (day, slot) => {
    return schedules.find(
      (s) => s.day_of_week === day && s.start_time === slot.start
    );
  };

  const getSubjectColor = (subjectName) => {
    const colors = [
      'bg-blue-100 border-blue-400 text-blue-900',
      'bg-green-100 border-green-400 text-green-900',
      'bg-purple-100 border-purple-400 text-purple-900',
      'bg-yellow-100 border-yellow-400 text-yellow-900',
      'bg-pink-100 border-pink-400 text-pink-900',
      'bg-indigo-100 border-indigo-400 text-indigo-900',
      'bg-red-100 border-red-400 text-red-900',
      'bg-teal-100 border-teal-400 text-teal-900',
    ];
    let hash = 0;
    for (let i = 0; i < (subjectName || '').length; i++) {
      hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="p-6">
      {/* Styles d'animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          📅 Gestion de l'emploi du temps
        </h1>
        <p className="text-gray-500 mt-1">
          Créez et modifiez l'emploi du temps hebdomadaire de chaque classe
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Classe
          </label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
          >
            <option value="">-- Sélectionner une classe --</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.students_count || 0} élèves)
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[180px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Année scolaire
          </label>
          <input
            type="text"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2"
            placeholder="2026-2027"
          />
        </div>

        <button
          onClick={loadSchedules}
          disabled={!selectedClass}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          🔍 Charger
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-center py-8 text-gray-500">Chargement...</div>
      )}

      {selectedClass && !loading && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 w-24">
                  Heure
                </th>
                {DAYS.map((d) => (
                  <th
                    key={d}
                    className="border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700"
                  >
                    {DAY_LABELS[d]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIME_SLOTS.map((slot) => (
                <tr key={slot.start}>
                  <td className="border border-gray-200 px-3 py-3 text-xs font-mono text-gray-600 bg-gray-50 text-center">
                    {slot.start}
                    <br />↓<br />
                    {slot.end}
                  </td>
                  {DAYS.map((day) => {
                    const schedule = getScheduleForSlot(day, slot);
                    const isPause = slot.start === '12:00';

                    if (isPause && !schedule) {
                      return (
                        <td
                          key={day}
                          className="border border-gray-200 px-2 py-4 text-center text-xs text-gray-400 bg-gray-50"
                        >
                          PAUSE
                        </td>
                      );
                    }

                    return (
                      <td
                        key={day}
                        className="border border-gray-200 px-1 py-1 align-top"
                        style={{ minWidth: '130px' }}
                      >
                        {schedule ? (
                          <div
                            className={`rounded border-l-4 p-2 text-xs ${getSubjectColor(
                              schedule.subject_name
                            )}`}
                          >
                            <div className="font-bold">{schedule.subject_name}</div>
                            <div className="text-[11px] opacity-80">
                              👨‍🏫 {schedule.teacher_name}
                            </div>
                            {schedule.room && (
                              <div className="text-[11px] opacity-80">
                                🚪 {schedule.room}
                              </div>
                            )}
                            <div className="flex gap-1 mt-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openEditModal(schedule);
                                }}
                                className="flex-1 text-[11px] bg-white hover:bg-blue-100 border border-blue-300 px-2 py-1 rounded font-medium transition"
                                title="Modifier ce cours"
                              >
                                ✏️ Modifier
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDelete(schedule.id);
                                }}
                                className="flex-1 text-[11px] bg-white hover:bg-red-100 border border-red-300 px-2 py-1 rounded font-medium transition"
                                title="Supprimer ce cours"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openCreateModal(day, slot)}
                            className="w-full h-full min-h-[60px] text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded text-2xl"
                            title="Ajouter un cours"
                          >
                            +
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!selectedClass && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-8 rounded text-center">
          👆 Sélectionnez une classe pour afficher son emploi du temps
        </div>
      )}

      {/* ========== MODAL AJOUT / MODIFICATION ========== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {editingId ? '✏️ Modifier le cours' : '➕ Ajouter un cours'}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Matière *
                </label>
                <select
                  required
                  value={form.subject_id}
                  onChange={(e) =>
                    setForm({ ...form, subject_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">-- Choisir --</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (coef {s.coefficient})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Professeur
                </label>
                <select
                  value={form.teacher_id}
                  onChange={(e) =>
                    setForm({ ...form, teacher_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">-- Aucun --</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jour *
                  </label>
                  <select
                    required
                    value={form.day_of_week}
                    onChange={(e) =>
                      setForm({ ...form, day_of_week: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    {DAYS.map((d) => (
                      <option key={d} value={d}>
                        {DAY_LABELS[d]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salle
                  </label>
                  <input
                    type="text"
                    value={form.room}
                    onChange={(e) =>
                      setForm({ ...form, room: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Salle 12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Heure début *
                  </label>
                  <input
                    type="time"
                    required
                    value={form.start_time}
                    onChange={(e) =>
                      setForm({ ...form, start_time: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Heure fin *
                  </label>
                  <input
                    type="time"
                    required
                    value={form.end_time}
                    onChange={(e) =>
                      setForm({ ...form, end_time: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded"
                >
                  {editingId ? '💾 Modifier' : '✅ Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========== NOTIFICATION ========== */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}